/**
 * smartVisitPlanner.js
 * Priority-based visit suggestions from relationship memory + district signals.
 *
 * No location data in the schema, so suggestions are ordered by:
 *   1. Longest without contact + highest risk (relationship memory)
 *   2. Active stagnant workflows with a visit component
 *   3. Families flagged in district insights
 *
 * Output: pastoral visit suggestions with rationale.
 * Language: warm and ministerial, never logistical.
 */

import { getAllEntityHistories }  from "../kevia-relationships/relationalMemoryEngine";
import { getAttentionItems }      from "../kevia-data/districtStateEngine";
import { getActiveWorkflows }     from "../kevia-workflows/workflowState";
import { WORKFLOW_TYPES }         from "../kevia-workflows/workflowSchemas";

const VISIT_WORKFLOW_TYPES = new Set([
  WORKFLOW_TYPES.FAMILY_FOLLOWUP,
  WORKFLOW_TYPES.VISIT_CYCLE,
  WORKFLOW_TYPES.INACTIVE_MEMBER_RECOVERY,
]);

const MAX_SUGGESTIONS = 4;

/**
 * @typedef {{
 *   name:   string,
 *   type:   "family" | "person" | "unknown",
 *   days:   number | null,
 *   reason: string,
 *   score:  number,
 * }} VisitSuggestion
 */

/**
 * Build a prioritized list of pastoral visit suggestions.
 *
 * @returns {{ suggestions: VisitSuggestion[], compact: string | null }}
 */
export function buildVisitSuggestions() {
  const suggestions = [];
  const seen        = new Set();

  // ── Priority 1: entities from relationship memory at high risk ────────────
  const histories = getAllEntityHistories();
  for (const record of histories) {
    if (suggestions.length >= MAX_SUGGESTIONS) break;
    const recent = record.signals?.[0];
    if (!recent) continue;

    const isAtRisk = recent.tone === "followup_urgent" || recent.tone === "sensitive";
    if (!isAtRisk || seen.has(record.name)) continue;

    const days = recent.visitDays;
    const reason =
      recent.tone === "sensitive"
        ? "hay situaciones delicadas activas"
        : days != null
          ? `lleva ${days} días sin visita`
          : "sin seguimiento reciente";

    suggestions.push({
      name:  record.name,
      type:  record.entityType,
      days,
      reason,
      score: recent.tone === "sensitive" ? 0.90 : 0.80,
    });
    seen.add(record.name);
  }

  // ── Priority 2: entities with stagnant visit workflows ────────────────────
  const workflows = getActiveWorkflows().filter((w) => VISIT_WORKFLOW_TYPES.has(w.type));
  for (const wf of workflows) {
    if (suggestions.length >= MAX_SUGGESTIONS) break;
    const entityName = wf.related_entities?.[0]?.name;
    if (!entityName || seen.has(entityName)) continue;

    const daysSinceActivity = Math.round((Date.now() - wf.last_activity) / 86_400_000);
    if (daysSinceActivity < 7) continue; // only suggest stagnant ones

    suggestions.push({
      name:   entityName,
      type:   "unknown",
      days:   daysSinceActivity,
      reason: "proceso de seguimiento activo sin avance reciente",
      score:  0.65,
    });
    seen.add(entityName);
  }

  if (suggestions.length === 0) {
    return { suggestions: [], compact: null };
  }

  // Sort by score and build compact string
  suggestions.sort((a, b) => b.score - a.score);

  const names   = suggestions.slice(0, 3).map((s) => s.name).join(", ");
  const compact = `Podría ser buen momento para visitar: ${names}.`;

  return { suggestions, compact };
}
